import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import Placeholder from "@tiptap/extension-placeholder";
import { useState, useEffect, useCallback } from "react";
import {
  Bold,
  Italic,
  Underline as UnderlineIcon,
  Heading3,
  Heading4,
  List,
  ListOrdered,
  Braces,
  Minus,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import TurndownService from "turndown";
import Showdown from "showdown";

const turndown = new TurndownService({
  headingStyle: "atx",
  bulletListMarker: "-",
});
turndown.addRule("underline", {
  filter: ["u"],
  replacement: (content) => `<u>${content}</u>`,
});

const showdownConverter = new Showdown.Converter({
  simpleLineBreaks: true,
  strikethrough: true,
});

function markdownToHtml(md: string): string {
  if (!md) return "";
  let html = showdownConverter.makeHtml(md);
  html = html.replace(
    /\{\{(\w+)\}\}/g,
    '<span data-placeholder="$1" class="placeholder-pill">{{$1}}</span>'
  );
  return html;
}

function htmlToMarkdown(html: string): string {
  if (!html) return "";
  let cleaned = html.replace(
    /<span[^>]*data-placeholder="(\w+)"[^>]*>.*?<\/span>/g,
    "{{$1}}"
  );
  cleaned = cleaned.replace(
    /<span[^>]*class="placeholder-pill"[^>]*>\{\{(\w+)\}\}<\/span>/g,
    "{{$1}}"
  );
  return turndown.turndown(cleaned);
}

function ToolbarButton({
  onClick,
  isActive,
  icon: Icon,
  title,
  testId,
}: {
  onClick: () => void;
  isActive?: boolean;
  icon: any;
  title: string;
  testId: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`p-1.5 rounded transition-colors ${
        isActive
          ? "bg-primary/20 text-primary"
          : "text-muted-foreground hover:bg-muted hover:text-foreground"
      }`}
      title={title}
      data-testid={testId}
    >
      <Icon className="w-4 h-4" />
    </button>
  );
}

export default function FormatTemplateEditor({
  value,
  onChange,
  readOnly = false,
  testIdPrefix = "format-editor",
}: {
  value: string;
  onChange: (markdown: string) => void;
  readOnly?: boolean;
  testIdPrefix?: string;
}) {
  const [placeholderName, setPlaceholderName] = useState("");
  const [popoverOpen, setPopoverOpen] = useState(false);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [3, 4] },
      }),
      Underline,
      Placeholder.configure({
        placeholder: "Design your format template here...",
      }),
    ],
    content: markdownToHtml(value),
    editable: !readOnly,
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      const md = htmlToMarkdown(html);
      onChange(md);
    },
  });

  useEffect(() => {
    if (editor && readOnly !== !editor.isEditable) {
      editor.setEditable(!readOnly);
    }
  }, [editor, readOnly]);

  useEffect(() => {
    if (editor && readOnly) {
      const currentHtml = editor.getHTML();
      const newHtml = markdownToHtml(value);
      if (currentHtml !== newHtml) {
        editor.commands.setContent(newHtml);
      }
    }
  }, [editor, value, readOnly]);

  const insertPlaceholder = useCallback(() => {
    if (!editor || !placeholderName.trim()) return;
    const name = placeholderName.trim().replace(/\s+/g, "_").toLowerCase();
    editor
      .chain()
      .focus()
      .insertContent(
        `<span data-placeholder="${name}" class="placeholder-pill">{{${name}}}</span>&nbsp;`
      )
      .run();
    setPlaceholderName("");
    setPopoverOpen(false);
  }, [editor, placeholderName]);

  if (!editor) return null;

  if (readOnly) {
    return (
      <div
        className="tiptap-readonly bg-muted/50 p-3 rounded text-sm"
        data-testid={`${testIdPrefix}-readonly`}
      >
        <EditorContent editor={editor} />
      </div>
    );
  }

  return (
    <div
      className="border border-border rounded-md overflow-hidden"
      data-testid={`${testIdPrefix}-container`}
    >
      <div className="flex items-center gap-0.5 px-2 py-1.5 border-b border-border bg-muted/30 flex-wrap">
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleBold().run()}
          isActive={editor.isActive("bold")}
          icon={Bold}
          title="Bold"
          testId={`${testIdPrefix}-bold`}
        />
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleItalic().run()}
          isActive={editor.isActive("italic")}
          icon={Italic}
          title="Italic"
          testId={`${testIdPrefix}-italic`}
        />
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleUnderline().run()}
          isActive={editor.isActive("underline")}
          icon={UnderlineIcon}
          title="Underline"
          testId={`${testIdPrefix}-underline`}
        />
        <div className="w-px h-5 bg-border mx-1" />
        <ToolbarButton
          onClick={() =>
            editor.chain().focus().toggleHeading({ level: 3 }).run()
          }
          isActive={editor.isActive("heading", { level: 3 })}
          icon={Heading3}
          title="Heading 3"
          testId={`${testIdPrefix}-h3`}
        />
        <ToolbarButton
          onClick={() =>
            editor.chain().focus().toggleHeading({ level: 4 }).run()
          }
          isActive={editor.isActive("heading", { level: 4 })}
          icon={Heading4}
          title="Heading 4"
          testId={`${testIdPrefix}-h4`}
        />
        <div className="w-px h-5 bg-border mx-1" />
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          isActive={editor.isActive("bulletList")}
          icon={List}
          title="Bullet List"
          testId={`${testIdPrefix}-bullet`}
        />
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          isActive={editor.isActive("orderedList")}
          icon={ListOrdered}
          title="Numbered List"
          testId={`${testIdPrefix}-ordered`}
        />
        <ToolbarButton
          onClick={() => editor.chain().focus().setHorizontalRule().run()}
          icon={Minus}
          title="Horizontal Rule"
          testId={`${testIdPrefix}-hr`}
        />
        <div className="w-px h-5 bg-border mx-1" />
        <Popover open={popoverOpen} onOpenChange={setPopoverOpen}>
          <PopoverTrigger asChild>
            <button
              type="button"
              className="flex items-center gap-1 px-2 py-1 rounded text-xs font-medium text-primary hover:bg-primary/10 transition-colors"
              title="Insert Placeholder"
              data-testid={`${testIdPrefix}-insert-placeholder`}
            >
              <Braces className="w-3.5 h-3.5" />
              Placeholder
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-64 p-3" align="start">
            <div className="space-y-2">
              <label className="text-xs font-medium text-muted-foreground">
                Placeholder name
              </label>
              <Input
                value={placeholderName}
                onChange={(e) => setPlaceholderName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    insertPlaceholder();
                  }
                }}
                placeholder="e.g. chief_complaint"
                className="text-sm h-8"
                autoFocus
                data-testid={`${testIdPrefix}-placeholder-input`}
              />
              <Button
                size="sm"
                className="w-full h-7 text-xs"
                onClick={insertPlaceholder}
                disabled={!placeholderName.trim()}
                data-testid={`${testIdPrefix}-placeholder-insert-btn`}
              >
                Insert
              </Button>
            </div>
          </PopoverContent>
        </Popover>
      </div>
      <div className="p-3">
        <EditorContent
          editor={editor}
          className="tiptap-editor text-sm min-h-[120px]"
          data-testid={`${testIdPrefix}-content`}
        />
      </div>
    </div>
  );
}
